import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ArrowRight, PartyPopper, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const isUpgrade = searchParams.get("upgraded") === "true";
  const changeType = searchParams.get("change") || "upgrade";
  const isDowngrade = changeType === "downgrade";
  const upgradeUsers = searchParams.get("users");
  const upgradeAmount = searchParams.get("amount");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (isUpgrade) { setStatus("paid"); return; }
    if (!sessionId) { setStatus("error"); return; }

    let attempts = 0;
    const maxAttempts = 15;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/stripe/checkout/status/${sessionId}`, { withCredentials: true });
        setPaymentData(res.data);

        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.data.status === "complete" || res.data.status === "expired") {
          setStatus(res.data.payment_status === "paid" ? "paid" : "pending");
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("pending");
        }
      } catch {
        setStatus("error");
      }
    };

    poll();
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Confirmando seu pagamento...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-md w-full p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Algo deu errado</h1>
          <p className="text-sm text-slate-500 mb-6">
            Não conseguimos confirmar o pagamento. Se o valor foi debitado, entre em contato conosco.
          </p>
          <div className="space-y-2">
            <Button onClick={() => navigate("/checkout")} className="w-full bg-indigo-600 text-white hover:bg-indigo-700">
              Tentar novamente
            </Button>
            <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full">
              Voltar ao CRM
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "paid") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-indigo-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <PartyPopper className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {isUpgrade ? (isDowngrade ? "Downgrade realizado!" : "Upgrade realizado!") : "Pagamento confirmado!"}
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            {isUpgrade
              ? (isDowngrade
                ? `Seu plano foi reduzido para ${upgradeUsers} usuário(s). O ajuste será refletido na próxima cobrança.`
                : `Seu plano foi atualizado para ${upgradeUsers} usuário(s). O valor proporcional será cobrado automaticamente.`)
              : "Sua assinatura está ativa. Aproveite todas as funcionalidades do Pipe4.me!"}
          </p>
          {!isUpgrade && paymentData?.amount_total && (
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Valor pago</span>
                <span className="font-bold text-slate-900">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </span>
              </div>
            </div>
          )}
          <Button
            data-testid="success-go-dashboard"
            onClick={() => navigate("/dashboard")}
            className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl"
          >
            Acessar o CRM <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // pending
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-md w-full p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-7 h-7 text-amber-600" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Pagamento pendente</h1>
        <p className="text-sm text-slate-500 mb-6">
          Seu pagamento ainda está sendo processado. Você receberá uma confirmação em breve.
        </p>
        <Button onClick={() => navigate("/dashboard")} className="w-full bg-slate-900 text-white hover:bg-slate-800">
          Voltar ao CRM
        </Button>
      </div>
    </div>
  );
}
